import { gsap } from 'gsap';
import React, {
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { Chip, Dropdown, Fade, Icon, formatNumber } from '@column/column-ui-kit';

import { TransferAmount } from '~/app/pages/Transfers/Overview';
import { LogoLoading } from '~/elements';
import { useQueryParams } from '~/hooks';
import { TransferFilterType, useTransfers } from '~/hooks/useTransfers';
import { TransferEntry, TransferListResponse, convertTransferAmount } from '~/repositories/Transfer';
import { useSessionStore } from '~/stores/Session';
import { TableBottomFade } from '~/styles';

import { Pagination } from './Pagination';
import { Table, TableColumn } from './Table';

// This is very WIP and working for transfers only currently - will become the base for any overview table in the future.
// The goal and improvements are:
// - use the new generalized API look
// - avoid uneccessary re-renders, improve performance
// - add consistent filter and pagination functionality using the URL query params as the state
// - many more improvements to come

// Going to outline and document this further once I got more time!
// So right now it's not generic at all and only works for transfers.

export interface OverviewTableProps {
  tableId: string;
  className?: string;
  fetchFilter?: TransferFilterType | null;
  persistParams?: Omit<TransferFilterType, 'page'>;
  onResponse?: (response?: TransferListResponse) => void;
  onLimitChange?: (limit: number) => void;
  columns: TableColumn[];
  action?: ReactNode;
  empty?: ReactNode;
  rowClick?: (row: any) => void;
}

export interface OverviewTableRefProps {
  fetch: (params?: TransferFilterType) => void;
  reset: () => void;
  setQueryParams: (object?: TransferFilterType) => void;
}

const Container = styled.div`
  position: relative;
`;

const Wrapper = styled.div`
  will-change: height;
  transform: translateZ(0);
  position: relative;
  z-index: 2;
`;

const StyledLoading = styled(LogoLoading)`
  top: 80px;
`;

const Entries = styled(Chip)`
  --chip-pale: ${({ theme }) => theme.secondary.blendToBackground(150)};

  svg {
    display: inline-block;
    vertical-align: top;
    margin: 0 0 0 -4px;
  }
`;

export const OverviewTable = forwardRef<OverviewTableRefProps, PropsWithChildren<OverviewTableProps>>((props, ref) => {
  const { updateSettings } = useSessionStore();

  const { queryParams, setQueryParams: setUrlQueryParams } = useQueryParams<TransferFilterType | undefined>();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (queryParams?.startingAfter || queryParams?.endingBefore) {
        setUrlQueryParams((prev) => {
          if (!prev) return { page: 1 };
          const cleaned = { ...prev, page: 1 };
          delete cleaned.startingAfter;
          delete cleaned.endingBefore;
          return cleaned;
        });
      }
    }
  }, []);

  const setQueryParams = useCallback(
    (newParams: TransferFilterType | undefined, overwrite?: boolean) => {
      if (overwrite) {
        setUrlQueryParams(() => newParams);
        return;
      }
      setUrlQueryParams((prev) => (!prev ? newParams : { ...prev, ...newParams }));
    },
    [setUrlQueryParams]
  );

  const apiQueryParams = useMemo(() => {
    const params = { ...queryParams, ...props.persistParams };
    Object.keys(params).forEach((key) => {
      if (params[key as keyof TransferFilterType] === undefined || key === 'page') {
        delete params[key as keyof TransferFilterType];
      }
    });
    return params;
  }, [queryParams, props.persistParams]);

  const { data: response, isLoading, isFetching, refetch } = useTransfers(apiQueryParams);

  useEffect(() => {
    if (response && props.onResponse) {
      props.onResponse(response);
    }
  }, [response, props.onResponse]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const fadeRef = useRef<HTMLDivElement>(null);

  const [entriesOpen, setEntriesOpen] = useState<boolean>(false);

  useEffect(() => {
    const isResetRequest =
      props.fetchFilter === null ||
      (typeof props.fetchFilter === 'object' &&
        props.fetchFilter !== null &&
        Object.keys(props.fetchFilter).length === 0);

    if (isResetRequest) {
      setQueryParams(
        {
          limit: queryParams?.limit,
          page: 1,
        },
        true
      );

      return;
    }

    if (!isLoading && JSON.stringify(props.fetchFilter) !== JSON.stringify(queryParams)) {
      setQueryParams({
        ...props.fetchFilter,
        startingAfter: undefined,
        endingBefore: undefined,
        page: 1,
      });

      return;
    }

    setQueryParams(props.fetchFilter ?? undefined);
  }, [props.fetchFilter]);

  const handlePrev = useCallback(() => {
    setQueryParams({
      startingAfter: undefined,
      endingBefore: Number(queryParams?.page) - 1 === 1 ? undefined : response?.transfers[0].id,
      page: Number(queryParams?.page ?? 1) - 1,
    });
  }, [queryParams, response?.transfers, setQueryParams]);

  const handleNext = useCallback(() => {
    setQueryParams({
      startingAfter:
        response?.transfers && Array.isArray(response?.transfers) ? response.transfers.at(-1)?.id : undefined,
      endingBefore: undefined,
      page: Number(queryParams?.page ?? 1) + 1,
    });
  }, [queryParams, response?.transfers, setQueryParams]);

  useImperativeHandle(
    ref,
    () => ({
      setQueryParams,
      reset: () => {
        setQueryParams(
          {
            limit: queryParams?.limit,
            startingAfter: undefined,
            endingBefore: undefined,
            page: 1,
          },
          true
        );
      },
      fetch: (params?: TransferFilterType) => {
        if (params) {
          setQueryParams({
            ...queryParams,
            ...params,
          });
        } else {
          refetch();
        }
      },
    }),
    [queryParams, refetch, setQueryParams]
  );

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) =>
          !state.settings.tableSettings || !state.settings.tableSettings[props.tableId]
            ? 10
            : state.settings.tableSettings[props.tableId].limit,
        (limit) => {
          if (limit !== queryParams?.limit) {
            setQueryParams({
              limit,
            });
          }
        },
        {
          fireImmediately: true,
        }
      ),
    [queryParams, setQueryParams]
  );

  const handleOnExit = () => {
    gsap.set(wrapperRef.current, {
      height: fadeRef.current?.offsetHeight,
    });
  };

  const handleOnEnter = () => {
    gsap.to(wrapperRef.current, {
      height: fadeRef.current?.offsetHeight,
      duration: 0.4,
      clearProps: 'height',
    });
  };

  const entryObject = useCallback(
    (entries: TransferListResponse['transfers']) => {
      return entries.map((entry: TransferEntry) => {
        const object: Partial<TransferEntry> = {};

        const convertAmount = convertTransferAmount(entry.type, entry.isIncoming);

        let { description } = entry;
        const checkNumber = entry.auxiliaryOnUs ?? entry.checkNumber;
        if ((entry.type === 'check_debit' || entry.type === 'check_credit') && checkNumber) {
          description = `#${checkNumber}${description ? ` ${description}` : ''}`;
        }

        return {
          id: entry.id,
          ...object,
          description: [
            <TransferAmount
              key={entry.id}
              $isAdding={
                entry.type === 'book'
                  ? props.fetchFilter?.bankAccountId || props.persistParams?.bankAccountId
                    ? entry.senderInternalAccount.bankAccountId ===
                      (props.fetchFilter?.bankAccountId || props.persistParams?.bankAccountId)
                      ? false
                      : true
                    : undefined
                  : convertAmount !== -1
              }
            >
              {formatNumber(entry?.amount ?? 0)}
            </TransferAmount>,
            description,
          ],
          status: entry.status,
          type: entry.type,
          incoming:
            entry.type === 'check_credit'
              ? 'Deposit'
              : entry.type === 'check_debit'
                ? 'Issue'
                : entry.type === 'book'
                  ? 'Internal'
                  : entry.isIncoming
                    ? 'Incoming'
                    : 'Outgoing',
          created: entry.createdAt,
        };
      });
    },
    [props.fetchFilter]
  );

  return (
    <Container>
      <Fade show={isLoading} base={StyledLoading} />
      <Wrapper ref={wrapperRef}>
        <Fade show={!isLoading} ref={fadeRef} onExit={handleOnExit} onEnter={handleOnEnter}>
          {props.empty && !isLoading && (response?.transfers || []).length < 1 && !props.fetchFilter ? (
            props.empty
          ) : (
            <>
              {props.children}
              <Table
                className={props.className}
                action={props.action}
                onRowClick={props.rowClick}
                columns={props.columns}
                data={response?.transfers ? entryObject(response?.transfers) : []}
                isLoading={isFetching && !isLoading}
              />
            </>
          )}
        </Fade>
      </Wrapper>
      <TableBottomFade show={!isLoading && (response?.transfers || []).length >= 1} timeoutEnter={400}>
        <Dropdown
          options={[5, 10, 15, 20, 25, 50].map((entries: number) => ({
            label: entries.toString(),
            value: entries,
          }))}
          size="small"
          variant="muted"
          active={queryParams?.limit ?? 10}
          customLabel={
            <Entries counter={queryParams?.limit ?? 10}>
              {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
            </Entries>
          }
          onOpenChange={setEntriesOpen}
          onChange={(value: number) => {
            updateSettings({
              tableSettings: {
                [props.tableId]: {
                  limit: value,
                },
              },
            }).then(() => {
              if (props.onLimitChange) {
                props.onLimitChange(value);
              }
              setQueryParams(
                {
                  startingAfter: undefined,
                  endingBefore: undefined,
                  limit: value,
                  page: 1,
                },
                true
              );
            });
          }}
        />

        <Pagination
          disablePrev={Number(queryParams?.page ?? 1) === 1}
          disableNext={!response?.hasMore}
          current={Number(queryParams?.page ?? 1)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      </TableBottomFade>
    </Container>
  );
});
